import { addToast } from '@heroui/toast';
import { useEffect, useRef, useState } from 'react';

import type { SiteDto } from '@/api/generated/schemas';
import { useUpdateSite } from '@/queries/sites.queries';
import { getFieldValue, prepareFieldUpdate, validateField } from '@/utils/siteUpdateHelpers';

export interface UseEditableFieldOptions<T> {
  siteId: string;
  site: SiteDto;
  fieldPath: string;
  label: string;
  validator?: (value: T) => string | null;
  onSaveSuccess?: () => void;
}

export interface UseEditableFieldReturn<T> {
  currentValue: string | number | boolean | null | undefined;
  isEditing: boolean;
  editValue: T | '';
  error: string | null;
  isSaving: boolean;
  setEditValue: (value: T | '') => void;
  setError: (error: string | null) => void;
  enterEditMode: () => void;
  handleSave: () => Promise<void>;
  handleCancel: () => void;
  handleKeyDown: (e: React.KeyboardEvent) => void;
  inputRef: React.RefObject<HTMLInputElement | null>;
}

export function useEditableField<T extends string | number>({
  siteId,
  site,
  fieldPath,
  label,
  validator,
  onSaveSuccess,
}: UseEditableFieldOptions<T>): UseEditableFieldReturn<T> {
  const currentValue = getFieldValue(fieldPath, site);
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState<T | ''>('');
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const updateMutation = useUpdateSite();

  // Initialize editValue when entering edit mode
  const enterEditMode = () => {
    const initialValue = currentValue === null || currentValue === undefined ? '' : (currentValue as T);
    setEditValue(initialValue);
    setIsEditing(true);
  };

  // Auto-focus input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  const handleSave = async () => {
    // Prepare value for saving (will be processed by prepareFieldUpdate)
    const valueToSave: string | number | boolean | null = editValue === '' ? null : editValue;

    // Check if value actually changed
    const hasChanged = valueToSave !== currentValue;
    if (!hasChanged) {
      setIsEditing(false);
      setError(null);
      return;
    }

    // Validate
    const validationError =
      validator && editValue !== '' ? validator(editValue as T) : validateField(fieldPath, valueToSave);
    if (validationError) {
      setError(validationError);
      return;
    }

    setError(null);

    // Prepare update payload
    const updatePayload = prepareFieldUpdate(fieldPath, valueToSave, site);

    try {
      await updateMutation.mutateAsync({
        siteId,
        data: updatePayload,
      });

      addToast({
        title: 'Updated',
        description: `${label} updated successfully`,
        color: 'success',
      });

      setIsEditing(false);
      onSaveSuccess?.();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update field';
      setError(errorMessage);
      addToast({
        title: 'Error',
        description: errorMessage,
        color: 'danger',
      });
    }
  };

  const handleCancel = () => {
    setError(null);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleCancel();
    } else if (e.key === 'Enter') {
      handleSave();
    }
  };

  return {
    currentValue,
    isEditing,
    editValue,
    error,
    isSaving: updateMutation.isPending,
    setEditValue,
    setError,
    enterEditMode,
    handleSave,
    handleCancel,
    handleKeyDown,
    inputRef,
  };
}
